import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Divider,
  LinearProgress,
  InputAdornment,
  Collapse,
  Avatar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import BarChartIcon from "@mui/icons-material/BarChart";
import PhoneIcon from "@mui/icons-material/Phone";
import BlockIcon from "@mui/icons-material/Block";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import LoopIcon from "@mui/icons-material/Loop";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TimerIcon from "@mui/icons-material/Timer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { format, parseISO, differenceInSeconds } from "date-fns";

import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { useVoiceReport } from "./useVoiceReport";
import { GlassButtonLoading } from "../../themes/UiElements";

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color = "primary", subtitle }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      borderLeft: 4,
      borderColor: `${color}.main`,
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: 3 },
    }}
  >
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
            width: 36,
            height: 36,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1}>
            {value ?? 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.disabled" display="block">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// ─── Progress Metric Row ───────────────────────────────────────────────────────
const MetricRow = ({ label, value, total, color = "primary" }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box mb={1.5}>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>
          {value}{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            / {total} ({pct}%)
          </Typography>
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

// ─── Campaign Detail Modal ─────────────────────────────────────────────────────
const CampaignDetailModal = ({ campaign, open, onClose }) => {
  const { generateAndDownloadReport, isGenerating } = useVoiceReport();

  if (!campaign) return null;

  const duration = (() => {
    try {
      const secs = differenceInSeconds(
        parseISO(campaign.endTime.replace(" ", "T")),
        parseISO(campaign.scheduleTime.replace(" ", "T"))
      );
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${m}m ${s}s`;
    } catch {
      return "—";
    }
  })();

  const connectionRate =
    campaign.numbersProcessed > 0
      ? ((campaign.callsConnected / campaign.numbersProcessed) * 100).toFixed(1)
      : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BarChartIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {campaign.campaignName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {campaign.campaignId}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Top KPI Grid ── */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Uploaded"
              value={campaign.numbersUploaded}
              icon={<PhoneIcon fontSize="small" />}
              color="info"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Processed"
              value={campaign.numbersProcessed}
              icon={<CheckCircleIcon fontSize="small" />}
              color="success"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Connected"
              value={campaign.callsConnected}
              icon={<PhoneIcon fontSize="small" />}
              color="primary"
              subtitle={`${connectionRate}% rate`}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="DND Blocked"
              value={campaign.dndCount}
              icon={<BlockIcon fontSize="small" />}
              color="error"
            />
          </Grid>
        </Grid>

        {/* ── Secondary KPI Grid ── */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Pulses"
              value={campaign.pulses}
              icon={<TouchAppIcon fontSize="small" />}
              color="warning"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="DTMF Responses"
              value={campaign.dtmfCount}
              icon={<TouchAppIcon fontSize="small" />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Retries"
              value={campaign.retries}
              icon={<LoopIcon fontSize="small" />}
              color="warning"
              subtitle={`${campaign.retryCount} attempts`}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Failed"
              value={campaign.failedCount}
              icon={<BlockIcon fontSize="small" />}
              color="error"
            />
          </Grid>
        </Grid>

        {/* ── Progress Metrics ── */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>
            Funnel Breakdown
          </Typography>
          <MetricRow
            label="Processed / Uploaded"
            value={campaign.numbersProcessed}
            total={campaign.numbersUploaded}
            color="info"
          />
          <MetricRow
            label="Connected / Processed"
            value={campaign.callsConnected}
            total={campaign.numbersProcessed}
            color="success"
          />
          <MetricRow
            label="DTMF / Connected"
            value={campaign.dtmfCount}
            total={campaign.callsConnected || 1}
            color="secondary"
          />
          <MetricRow
            label="DND / Uploaded"
            value={campaign.dndCount}
            total={campaign.numbersUploaded}
            color="error"
          />
        </Card>

        {/* ── Timeline & Config ── */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                <CalendarTodayIcon
                  fontSize="small"
                  sx={{ mr: 0.5, verticalAlign: "middle" }}
                />
                Timeline
              </Typography>
              {[
                ["Scheduled", campaign.scheduleTime],
                ["Completed", campaign.endTime],
                ["Duration", duration],
              ].map(([k, v]) => (
                <Stack
                  key={k}
                  direction="row"
                  justifyContent="space-between"
                  mb={0.75}
                >
                  <Typography variant="body2" color="text.secondary">
                    {k}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {v}
                  </Typography>
                </Stack>
              ))}
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                <TimerIcon
                  fontSize="small"
                  sx={{ mr: 0.5, verticalAlign: "middle" }}
                />
                Configuration
              </Typography>
              {[
                ["Template ID", campaign.templateId || "None"],
                ["DTMF Key 1", campaign.dtmf1Count],
                ["DTMF Key 2", campaign.dtmf2Count],
                [
                  "Webhook Log",
                  campaign.webhookLog === "1" ? "Enabled" : "Disabled",
                ],
                ["TTS Count", campaign.ttsCount ?? 0],
              ].map(([k, v]) => (
                <Stack
                  key={k}
                  direction="row"
                  justifyContent="space-between"
                  mb={0.75}
                >
                  <Typography variant="body2" color="text.secondary">
                    {k}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {v}
                  </Typography>
                </Stack>
              ))}
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* ── AI Analysis Section ── */}
        {/* <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                AI-Powered Analysis
              </Typography>
            </Stack>
            <Button
              variant="contained"
              size="small"
              onClick={handleAiAnalysis}
              disabled={aiLoading}
              startIcon={
                aiLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
            >
              {aiReport ? "Re-Analyze" : "Generate Analysis"}
            </Button>
          </Stack>

          {aiError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {aiError}
            </Alert>
          )}

          {aiLoading && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Analyzing campaign data…
              </Typography>
            </Box>
          )}

          {aiReport && !aiLoading && (
            <Card
              variant="outlined"
              sx={{
                p: 2.5,
                bgcolor: "primary.50",
                borderColor: "primary.200",
                maxHeight: 380,
                overflowY: "auto",
              }}
            >
              {renderAiReport(aiReport)}
            </Card>
          )}

          {!aiReport && !aiLoading && (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "grey.300",
              }}
            >
              <AutoAwesomeIcon
                sx={{ fontSize: 40, color: "grey.400", mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Click "Generate Analysis" for an AI-powered deep dive into this
                campaign's performance.
              </Typography>
            </Box>
          )}
        </Box> */}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <GlassButtonLoading
          variant="outlined"
          loading={isGenerating}
          startIcon={<DownloadIcon />}
          onClick={() => generateAndDownloadReport(campaign.campaignId)}
        >
          Download Report
        </GlassButtonLoading>
      </DialogActions>
    </Dialog>
  );
};

// ─── Status Chip helper ────────────────────────────────────────────────────────
const statusLabel = (status) => {
  const map = {
    2: { label: "Completed", color: "success" },
    1: { label: "Running", color: "warning" },
    0: { label: "Pending", color: "default" },
  };
  return map[status] || { label: "Unknown", color: "default" };
};

// ─── Main Dashboard ────────────────────────────────────────────────────────────
const ReportDashboard = () => {
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  );
  const { generateAndDownloadReport, isGenerating } = useVoiceReport();
  const [endDate, setEndDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [filterConnected, setFilterConnected] = useState("all");
  const [filterDnd, setFilterDnd] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const {
    data: campaignsData,
    isLoading,
    isError,
    refetch,
  } = useApiEp(
    `/voice/campaigns/historical?startDate=${format(
      startDate,
      "yyyy-MM-dd"
    )}&endDate=${format(endDate, "yyyy-MM-dd")}`
  );

  const campaigns = campaignsData?.campaigns || [];

  // ── Filters ──
  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      const matchSearch =
        !search ||
        c.campaignName.toLowerCase().includes(search.toLowerCase()) ||
        String(c.campaignId).includes(search);
      const matchConnected =
        filterConnected === "all" ||
        (filterConnected === "yes" && c.callsConnected > 0) ||
        (filterConnected === "no" && c.callsConnected === 0);
      const matchDnd =
        filterDnd === "all" ||
        (filterDnd === "yes" && c.dndCount > 0) ||
        (filterDnd === "no" && c.dndCount === 0);
      return matchSearch && matchConnected && matchDnd;
    });
  }, [campaigns, search, filterConnected, filterDnd]);

  // ── Summary Totals ──
  const totals = useMemo(
    () => ({
      total: campaigns.length,
      uploaded: campaigns.reduce((s, c) => s + c.numbersUploaded, 0),
      connected: campaigns.reduce((s, c) => s + c.callsConnected, 0),
      dnd: campaigns.reduce((s, c) => s + c.dndCount, 0),
      dtmf: campaigns.reduce((s, c) => s + c.dtmfCount, 0),
    }),
    [campaigns]
  );

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Stack>

        {/* ── Summary KPIs ── */}
        {campaigns.length > 0 && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={2.4}>
              <StatCard
                label="Total Campaigns"
                value={totals.total}
                icon={<BarChartIcon fontSize="small" />}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <StatCard
                label="Numbers Uploaded"
                value={totals.uploaded}
                icon={<PhoneIcon fontSize="small" />}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <StatCard
                label="Calls Connected"
                value={totals.connected}
                icon={<CheckCircleIcon fontSize="small" />}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <StatCard
                label="DND Blocked"
                value={totals.dnd}
                icon={<BlockIcon fontSize="small" />}
                color="error"
              />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <StatCard
                label="DTMF Responses"
                value={totals.dtmf}
                icon={<TouchAppIcon fontSize="small" />}
                color="secondary"
              />
            </Grid>
          </Grid>
        )}

        {/* ── Filter Bar ── */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            {/* Primary row: date range + search */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              flexWrap="wrap"
            >
              <DatePicker
                label="From"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
              />
              <DatePicker
                label="To"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={refetch}
                disabled={isLoading}
                sx={{ height: 40 }}
              >
                {isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Apply"
                )}
              </Button>

              <Box flex={1} />

              <TextField
                size="small"
                placeholder="Search campaign name / ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 240 }}
              />

              <Tooltip title="More filters">
                <IconButton
                  onClick={() => setShowFilters((p) => !p)}
                  color={showFilters ? "primary" : "default"}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Expandable secondary filters */}
            <Collapse in={showFilters}>
              <Stack direction="row" spacing={2} mt={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Calls Connected</InputLabel>
                  <Select
                    value={filterConnected}
                    onChange={(e) => setFilterConnected(e.target.value)}
                    label="Calls Connected"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Has Connections</MenuItem>
                    <MenuItem value="no">No Connections</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>DND Numbers</InputLabel>
                  <Select
                    value={filterDnd}
                    onChange={(e) => setFilterDnd(e.target.value)}
                    label="DND Numbers"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Has DND</MenuItem>
                    <MenuItem value="no">No DND</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  size="small"
                  onClick={() => {
                    setSearch("");
                    setFilterConnected("all");
                    setFilterDnd("all");
                  }}
                >
                  Clear Filters
                </Button>
              </Stack>
            </Collapse>
          </CardContent>
        </Card>

        {/* ── Status & Errors ── */}
        {isLoading && <MuPb />}
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load campaigns. Please refresh.
          </Alert>
        )}

        {/* ── Campaign Table ── */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Showing {filtered.length} of {campaigns.length} campaigns
          </Typography>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 700 }}>Campaign</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Scheduled</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Uploaded
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Processed
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Connected
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  DND
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  DTMF
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Retries
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    {campaigns.length === 0
                      ? "No campaigns found for the selected date range."
                      : "No campaigns match your filters."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => {
                  const connRate =
                    c.numbersProcessed > 0
                      ? ((c.callsConnected / c.numbersProcessed) * 100).toFixed(
                          0
                        )
                      : 0;
                  const { label, color } = statusLabel(c.campaignStatus);
                  return (
                    <TableRow
                      key={c.campaignId}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: "primary.50" },
                      }}
                      onClick={() => setSelectedCampaign(c)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {c.campaignName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{c.campaignId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {c.scheduleTime.slice(0, 16)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{c.numbersUploaded}</TableCell>
                      <TableCell align="center">{c.numbersProcessed}</TableCell>
                      <TableCell align="center">
                        <Stack alignItems="center" spacing={0.25}>
                          <Typography
                            variant="body2"
                            fontWeight={c.callsConnected > 0 ? 700 : 400}
                            color={
                              c.callsConnected > 0
                                ? "success.main"
                                : "text.secondary"
                            }
                          >
                            {c.callsConnected}
                          </Typography>
                          {c.callsConnected > 0 && (
                            <Typography variant="caption" color="success.main">
                              {connRate}%
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={
                            c.dndCount > 0 ? "error.main" : "text.secondary"
                          }
                        >
                          {c.dndCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={
                            c.dtmfCount > 0
                              ? "secondary.main"
                              : "text.secondary"
                          }
                        >
                          {c.dtmfCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{c.retries}</TableCell>
                      <TableCell align="center">
                        <Chip label={label} color={color} size="small" />
                      </TableCell>
                      <TableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setSelectedCampaign(c)}
                            >
                              <BarChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            {isGenerating ? (
                              <CircularProgress />
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  generateAndDownloadReport(c.campaignId)
                                }
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Detail Modal ── */}
      <CampaignDetailModal
        campaign={selectedCampaign}
        open={!!selectedCampaign}
        onClose={() => setSelectedCampaign(null)}
      />
    </>
  );
};

export default ReportDashboard;
